import { Webinar } from "./types";

export type AvailabilityTone = "open" | "low" | "closed" | "past" | "cancelled";

export interface Availability {
  label: string;
  tone: AvailabilityTone;
  canRegister: boolean;
  isPast: boolean;
  isFull: boolean;
  /** null when the webinar has no capacity limit */
  seatsLeft: number | null;
  /** Shown instead of the form when registration isn't possible */
  closedReason?: string;
}

/** When registration stops: the deadline if one is set, otherwise the start time */
export const registrationClosesAt = (w: Webinar) => w.registrationDeadline ?? w.startAt;

export const isPastWebinar = (w: Webinar, now = Date.now()) =>
  w.status === "completed" || Date.parse(w.endAt) <= now;

/** Visible on the public pages at all (drafts never are) */
export const isPublicWebinar = (w: Webinar) => w.status !== "draft";

/** Public, not cancelled and not over yet, soonest first: the "Upcoming" lists on /events and /news */
export const upcomingWebinars = (webinars: Webinar[], now = Date.now()) =>
  webinars
    .filter(w => isPublicWebinar(w) && w.status !== "cancelled" && !isPastWebinar(w, now))
    .sort((a, b) => Date.parse(a.startAt) - Date.parse(b.startAt));

/**
 * Public registration state, checked in this order: cancelled, past, closed by the
 * administrator, deadline passed, full, few seats left, open. The repository applies
 * the same rules again when a registration is saved.
 */
export function availability(w: Webinar, activeRegistrations = 0, now = Date.now()): Availability {
  const seatsLeft = w.capacity ? Math.max(0, w.capacity - activeRegistrations) : null;
  const isFull = seatsLeft === 0;
  const isPast = isPastWebinar(w, now);
  const base = { isPast, isFull, seatsLeft, canRegister: false };

  if (w.status === "cancelled") {
    return { ...base, label: "Cancelled", tone: "cancelled", closedReason: "This webinar has been cancelled. Please choose another session." };
  }
  if (isPast) {
    return { ...base, label: "Past event", tone: "past", closedReason: "This webinar has taken place. See what is coming up next on the Upcoming page." };
  }
  if (w.status !== "published") {
    return { ...base, label: "Registration closed", tone: "closed", closedReason: "Registration for this webinar is closed. Please choose another session." };
  }
  if (Date.parse(registrationClosesAt(w)) <= now) {
    return { ...base, label: "Registration closed", tone: "closed", closedReason: "The registration deadline for this webinar has passed." };
  }
  if (isFull) {
    return { ...base, label: "Full", tone: "closed", closedReason: "This webinar is fully booked. Please choose another session." };
  }
  if (seatsLeft !== null && seatsLeft <= 10) {
    return { ...base, canRegister: true, label: `${seatsLeft} seats left`, tone: "low" };
  }
  return { ...base, canRegister: true, label: "Open", tone: "open" };
}
